import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Space, Typography, Alert } from 'antd';
import { AudioOutlined, StopOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from '../services/axiosInstance';
import { baseApi } from '../redux/api/baseApi';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

const { Text } = Typography;

const MAX_RECORDING_SECONDS = 300; // 5 minutes

const isMediaRecorderSupported = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
};

const VoiceNoteRecorder = ({ entityType, entityId, onNoteCreated }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(true);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isMediaRecorderSupported()) {
      setSupported(false);
      setError(
        'Voice recording is not available. Your browser may not support it, ' +
        'or the page must be served over HTTPS.'
      );
    }
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const stopRecordingCleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    setIsRequesting(false);
    setRecordingTime(0);
  }, []);

  const handleDataAvailable = useCallback((event) => {
    if (event.data && event.data.size > 0) {
      chunksRef.current.push(event.data);
    }
  }, []);

  const sendTranscription = useCallback(async (audioBlob) => {
    const formData = new FormData();

    const mimeType = audioBlob.type || 'audio/webm';
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    formData.append('audio', audioBlob, `recording.${extension}`);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    formData.append('language', 'cs');

    setIsTranscribing(true);

    try {
      const response = await axios.post('/notes/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      toast.success('Voice note transcribed successfully');
      dispatch(baseApi.util.invalidateTags([
        { type: 'Note', id: `${entityType}-${entityId}` },
      ]));
      if (onNoteCreated) {
        onNoteCreated(response.data);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Transcription failed';
      setError(msg);
      toast.error('Transcription failed: ' + msg);
    } finally {
      setIsTranscribing(false);
    }
  }, [entityType, entityId, onNoteCreated, dispatch]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    stopRecordingCleanup();
  }, [stopRecordingCleanup]);

  const startRecording = useCallback(async () => {
    setError(null);
    chunksRef.current = [];

    if (!isMediaRecorderSupported()) {
      setError(
        'Voice recording is not supported in this browser. ' +
        'Please use a modern browser (Chrome, Firefox, Edge, Safari) and ensure the page is served over HTTPS or localhost.'
      );
      return;
    }

    setIsRequesting(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = handleDataAvailable;

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'audio/webm'
        });
        chunksRef.current = [];

        if (audioBlob.size > 0) {
          await sendTranscription(audioBlob);
        }
      };

      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError('Recording error: ' + (event.error?.message || 'Unknown error'));
        stopRecordingCleanup();
      };

      recorder.start(1000);
      setIsRecording(true);
      setIsRequesting(false);

      let elapsed = 0;
      timerRef.current = setInterval(() => {
        elapsed += 1;
        setRecordingTime(elapsed);
        if (elapsed >= MAX_RECORDING_SECONDS) {
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      setIsRequesting(false);
      console.error('startRecording error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone access denied. Please allow microphone access in your browser settings and try again.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Microphone is already in use by another application. Please close it and try again.');
      } else if (err.name === 'TypeError') {
        setError('Voice recording is not available in this context. The page may need to be served over HTTPS.');
      } else {
        setError('Could not start recording: ' + (err.message || 'Unknown error'));
      }
    }
  }, [handleDataAvailable, sendTranscription, stopRecordingCleanup, stopRecording]);

  if (isTranscribing) {
    return (
      <Space align="center">
        <LoadingOutlined spin style={{ fontSize: 18 }} />
        <Text type="secondary">Transcribing voice note...</Text>
      </Space>
    );
  }

  return (
    <div>
      <Space align="center">
        {isRecording ? (
          <>
            <Button
              type="primary"
              danger
              icon={<StopOutlined />}
              onClick={stopRecording}
            >
              Stop Recording
            </Button>
            <Text type="danger" strong>
              <span style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#ff4d4f',
                marginRight: 6,
                animation: 'pulse 1s infinite',
              }} />
              {formatTime(recordingTime)}
            </Text>
          </>
        ) : isRequesting ? (
          <Space align="center">
            <LoadingOutlined spin style={{ fontSize: 16 }} />
            <Text type="secondary">Requesting microphone access...</Text>
          </Space>
        ) : (
          <Button
            icon={<AudioOutlined />}
            onClick={startRecording}
            disabled={!supported}
          >
            Record Voice Note
          </Button>
        )}
      </Space>
      {error && (
        <Alert
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginTop: 8 }}
        />
      )}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default VoiceNoteRecorder;
